import { Route } from "routes/_authenticated/app.uppdrag.$uuid.index";
import { UppdragOverview } from "./overview/UppdragOverview";
import { TransportSedelTab } from "./transportSedel/TransportSedelTab";
import { Bilagor } from "./bilagor/Bilagor";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { getUppdrag } from "api/uppdrag.api";
import { PageLoader } from "components/PageLoader";
import { PageHeader } from "components/PageHeader";
import { CommentLog } from "./commentLog/CommentLog";
import { TabMenu } from "./TabMenu";
import {
  getUppdragLabel,
  isNorva,
  shouldShowEmailToCustomerPing,
  shouldShowEmailToWasteStationPing,
  shouldShowWaitingForTimeReportConfirmationPing,
  Uppdrag,
} from "types/uppdrag.type";
import { UppdragPreviewModal } from "pages/app/uppdrag-list-page/operator-list/UppdragPreviewModal";
import { Naturvardsverket } from "./naturvardsverket/Naturvardsverket";
import { ErrorScreen } from "components/ErrorScreen";
import { NvRapporteringStatus } from "enums/NvRapporteringStatus.enum";
import { TimeReportModal } from "./TimeReportModal";
import { CompleteUppdragModal } from "components/completeUppdragModal/CompleteUppdragModal";
import { useCompleteUppdragOnUppgifterDone } from "./useCompleteUppdragOnUppgifterDone";
import { useCurrentCompany, useHasRightsForFeature } from "api/company.api";
import { useIsAdmin } from "store/user.store";
import { isArrayWithItems } from "helpers/array";
import { useEffect, useMemo } from "react";
import { scrollToTop } from "helpers/scroll";
import { Company, OperatorPrivilege } from "types/company.type";
import { Tab } from "types/tab.type";

export enum UppdragPageTab {
  Oversikt = "oversikt",
  TransportSedel = "transport-sedel",
  Bilagor = "bilagor",
  Naturvardsverket = "naturvardsverket",
}

export const UppdragPage = () => {
  const { uuid } = Route.useParams();
  const search = Route.useSearch();

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: currentCompany } = useCurrentCompany();

  const {
    data: uppdrag,
    isLoading,
    error,
  } = useQuery({
    ...getUppdrag(uuid),
  });

  const isAdmin = useIsAdmin();

  const {
    isModalOpen: isCompleteUppdragModalOpen,
    results: completeUppdragResults,
    closeModal: closeCompleteUppdragModal,
  } = useCompleteUppdragOnUppgifterDone(uppdrag);

  const tabList = getTabList(isAdmin, uppdrag, currentCompany);

  const currentTab = search.tab ?? UppdragPageTab.Oversikt;

  const tabMenuItems = useMemo(
    () =>
      tabList.map((tab) => ({
        label: tab.label,
        value: tab.value,
        isActive: currentTab === tab.value,
        ping: tab.ping,
        link: {
          search: { tab: tab.value },
        },
      })),
    [tabList, currentTab],
  );

  useEffect(() => {
    if (uppdrag) {
      document.title = `Uppdrag - ${uppdrag.facility.customer.name}`;
    }
  }, [uppdrag]);

  useEffect(() => {
    scrollToTop();
  }, [currentTab]);

  const canViewCustomerPage = useHasRightsForFeature(
    OperatorPrivilege.ViewCustomer,
  );

  if (isLoading) {
    return <PageLoader />;
  }

  if (error) {
    const status = (error as any)?.response?.status;
    if (status === 404) {
      return (
        <ErrorScreen
          title="Uppdraget hittades inte"
          message="Det uppdrag du letar efter finns inte eller har tagits bort."
          // errorCode="404"
        />
      );
    }
  }

  if (!uppdrag) {
    return <ErrorScreen />;
  }

  // Norvа-imported uppdrag that hasn't been claimed yet: an operator must
  // assign + start it before the page content is available. Block with the
  // preview modal until then; closing it sends them back to the list.
  const shouldPreviewBeforeStart =
    !isAdmin &&
    isNorva(uppdrag) &&
    !!uppdrag.externalId &&
    !uppdrag.assignedToUser;

  if (shouldPreviewBeforeStart) {
    return (
      <UppdragPreviewModal
        isOpen
        onClose={() => navigate({ to: "/app/uppdrag", replace: true })}
        uppdrag={uppdrag}
        onSuccess={() =>
          queryClient.invalidateQueries({
            queryKey: getUppdrag(uuid).queryKey,
          })
        }
      />
    );
  }

  return (
    <>
      <PageHeader
        header={getUppdragLabel(uppdrag)}
        breadCrumbList={[
          {
            label: uppdrag.facility.customer.name,
            link: canViewCustomerPage
              ? {
                  to: "/app/kunder/$customerId",
                  params: {
                    customerId: uppdrag.facility.customer.id.toString(),
                  },
                }
              : undefined,
            iconType: "customer",
          },
          {
            label: uppdrag.facility.name,
            link: {
              to: "/app/kunder/$customerId/facility/$facilityId",
              params: {
                customerId: uppdrag.facility.customer.id.toString(),
                facilityId: uppdrag.facility.id.toString(),
              },
            },
            iconType: "facility",
          },
        ]}
        uppdrag={uppdrag}
        // iconType="uppdrag"
        // uppdragStatus={uppdrag.status}
      />

      {/* Här är tabbarna */}
      <TabMenu items={tabMenuItems} className="mb-4" />
      <div className="grid w-full gap-4 lg:grid-cols-[1fr_300px]">
        {currentTab === UppdragPageTab.Oversikt && (
          <UppdragOverview uppdrag={uppdrag} />
        )}
        {currentTab === UppdragPageTab.TransportSedel && (
          <TransportSedelTab uppdrag={uppdrag} />
        )}
        {currentTab === UppdragPageTab.Bilagor && <Bilagor uppdrag={uppdrag} />}
        {currentTab === UppdragPageTab.Naturvardsverket && (
          <Naturvardsverket uppdrag={uppdrag} />
        )}
        <CommentLog uppdrag={uppdrag} />

        {/* {tab === "Overview" && } */}
        {/* {tab === "Kontrollrapporter" && } */}
      </div>
      <TimeReportModal uppdrag={uppdrag} />
      <CompleteUppdragModal
        isOpen={isCompleteUppdragModalOpen}
        onClose={closeCompleteUppdragModal}
        results={completeUppdragResults}
      />
    </>
  );
};

const getTabList = (
  isAdmin: boolean,
  uppdrag?: Uppdrag,
  currentCompany?: Company,
) => {
  const result: Tab<UppdragPageTab>[] = [
    {
      label: "Översikt",
      value: UppdragPageTab.Oversikt,
      ping: isAdmin && shouldShowWaitingForTimeReportConfirmationPing(uppdrag),
    },
  ];
  if (isArrayWithItems(uppdrag?.transportSedelList)) {
    result.push({
      label: `Transportsedel`,
      value: UppdragPageTab.TransportSedel,
    });
  }

  result.push({
    label: "Dokument",
    value: UppdragPageTab.Bilagor,
    ping:
      isAdmin &&
      (shouldShowEmailToCustomerPing(uppdrag) ||
        shouldShowEmailToWasteStationPing(uppdrag)),
  });
  if (
    currentCompany?.settings?.isNvActive &&
    isAdmin &&
    (uppdrag?.transportSedelList ?? []).length > 0
  ) {
    result.push({
      label: "Naturvårdsverket",
      value: UppdragPageTab.Naturvardsverket,
      ping:
        isAdmin &&
        uppdrag?.wasteList?.some(
          (x) =>
            x.ombudsRapporteringStatus ===
              NvRapporteringStatus.WaitingForReport ||
            x.transportRapporteringStatus ===
              NvRapporteringStatus.WaitingForReport,
        ),
    });
  }
  return result;
};
